import * as React from 'react'
import { connect } from 'react-redux'
import to from 'await-to-js'
import { compose } from 'redux'
import classnames from 'classnames'
import SVG from 'react-inlinesvg'
import StepBar from '@App/components/blocks/StepBar'
import FormatText from '@App/helpers/formatText'
import BACK_ICON from 'assets/icon-caret-black.svg'

import { Props } from './types'

import Informations from './Informations'
import Code from './Code'
import Congratulation from './Congratulation'

import './styles.scss'
import { setHideModal } from '@App/store/modal'
import { activateConfirmSecureUserAccount, activateSecureUserAccount } from '@App/actions/profiles'
import { disable2FAConfirm } from '@App/store/user'

export const SegmentContext = React.createContext({})

class DoubleAuthenticationForm extends React.Component<Props, any> {
  steps: any[]

  constructor(props: Props) {
    super(props)

    this.state = {
      step: props.step || 0,
      body: {
        country_code: '+33',
        phone: '',
        code: props?.user?.phone ? props?.user?.phone : '',
      },
    }

    this.resetState = this.resetState.bind(this)
    this.handleChangeStep = this.handleChangeStep.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.update = this.update.bind(this)

    this.onHandleSubmitInformations = this.onHandleSubmitInformations.bind(this)
    this.onHandleSubmitCode = this.onHandleSubmitCode.bind(this)
    this.onHandleConfirmation = this.onHandleConfirmation.bind(this)

    this.steps = []

    if (props.isDisable) {
      this.steps = [
        {
          name: 'Code',
          render: () => <Code onSubmit={this.onHandleSubmitCode} />,
        },
        {
          name: 'Congratulation',
          render: () => <Congratulation onSubmit={this.onHandleConfirmation} />,
        },
      ]
    } else {
      this.steps = [
        {
          name: 'Informations',
          render: () => <Informations onSubmit={this.onHandleSubmitInformations} />,
        },
        {
          name: 'Code',
          render: () => <Code onSubmit={this.onHandleSubmitCode} />,
        },
        {
          name: 'Congratulation',
          render: () => <Congratulation onSubmit={this.onHandleConfirmation} />,
        },
      ]
    }
  }

  async onHandleSubmitInformations(phone: string) {
    const { activateSecureUserAccount } = this.props

    if (phone) {
      const [err, data] = await to(activateSecureUserAccount({
        phone,
        country_code: this.state.body.country_code,
      }) as any)

      if (err) {
        return
      }

      this.setState({
        step: 1,
        body: {
          ...this.state.body,
          phone,
        },
      })
    }
  }

  async onHandleSubmitCode(code: string) {
    const { activateConfirmSecureUserAccount, isDisable, disable2FAConfirm } = this.props

    if (code && !isDisable) {
      const [err] = await to(activateConfirmSecureUserAccount({
        code,
      }) as any)

      if (err) {
        return
      }

      this.setState({
        step: 2,
        body: {
          ...this.state.body,
          code,
        },
      })
    }

    if (code && isDisable) {
      const [err] = await to(disable2FAConfirm({
        code,
      }) as any)

      if (err) {
        return
      }

      this.setState({
        step: 1,
        body: {
          ...this.state.body,
          code,
        },
      })
    }
  }

  onHandleConfirmation() {
    const { setHideModal } = this.props

    this.setState({
      step: 3,
    })

    setHideModal()
  }

  resetState() {
    this.setState({
      step: 0,
      body: {},
    })
  }

  componentDidMount(): void { }

  handleChangeStep(step: number) {

    if (this.props.step === 0) {
      this.setState({ step })
    }

    if (step === 0) {
      return
    }

    this.setState({ step })
  }

  async handleSubmit(body: any) {
    const {
      setHideModal,
    } = this.props

    this.setState({
      ...this.state,
      step: 3,
    })

    setHideModal()
  }

  handleCloseModal() {
    const { setHideModal } = this.props

    this.resetState()
    setHideModal()
  }

  update(segment: any, step: number) {
    this.setState({
      step,
      body: {
        ...this.state.body,
        ...segment,
      },
    })
  }

  render(): JSX.Element {
    const { step, body } = this.state

    const currentStep = this.steps && this.steps[step]

    return (
      <SegmentContext.Provider value={{ state: this.state, update: this.update }}>
        <div
          className={classnames('doubleAuthentication block block--row', step > 0 && 'doubleAuthentication--large')}
          style={this.state.step === 2 ? { height: '100%' } : {}}
        >
          {step > 0 && (
            <div
              role="button"
              tabIndex={-10}
              className="backButton"
              onKeyDown={() => { }}
              onClick={() => this.handleChangeStep(step - 1)}
            >
              <SVG src={FormatText.formatSVGBase64(BACK_ICON)} />
              <p>Back</p>
            </div>
          )}

          <StepBar
            currentStep={step}
            data={this.steps}
            handleChangeStep={this.handleChangeStep}
          />

          <SegmentContext.Consumer>
            {(context) => {
              return (
                currentStep &&
                <currentStep.render
                  {...this.props}
                  body={body}
                  context={context}
                  key={currentStep.name}
                  onSubmit={this.steps.length - 1 && this.handleSubmit}
                  onClose={this.handleCloseModal}
                />
              )
            }}
          </SegmentContext.Consumer>
        </div>
      </SegmentContext.Provider>
    )
  }
}

const mapDispatchToProps = (dispatch: any): any => {
  return {
    setHideModal: () => dispatch(setHideModal()),
    activateSecureUserAccount: body => dispatch(activateSecureUserAccount(body)),
    activateConfirmSecureUserAccount: code => dispatch(activateConfirmSecureUserAccount(code)),
    disable2FAConfirm: code => dispatch(disable2FAConfirm(code)),
  }
}

export default compose<any>(
  connect(null, mapDispatchToProps),
)(DoubleAuthenticationForm)
